import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, BookOpen, Trash2, Brain, Info, ShieldX } from 'lucide-react';
import { useVesselMind } from '../context/VesselMindContext';
import { loadPdfKnowledgeBase, searchKnowledgeBase, getRelevantSections } from '../services/pdfKnowledgeService';
import { codesPracticeDocuments } from '../../data/pdfDocuments';
import { isClientInitialized, sendChatCompletionStream, validateContext } from '../services/anthropicClient';
import { checkRateLimit, recordRequest, startRequest, endRequest } from '../utils/rateLimiter';
import {
  isLearningModeEnabled,
  enableLearningMode,
  disableLearningMode,
  storeQAPair,
  getRelevantPastQA,
  improveDocumentRanking,
  getLearningStats,
  clearLearningData
} from '../services/learningService';
import { checkFAQCache, logAPIResponse } from '../services/faqService';
import { isCacheValid, loadFromCache, saveToCache } from '../services/knowledgeBaseCache';
import { runDiagnostics } from '../utils/diagnosticSearch';
// Document filtering disabled - caused accuracy issues
// import { filterDocumentsByQuery } from '../services/documentFilterService';
// ApiKeySetup removed - not currently used
import AddToFAQModal from './AddToFAQModal';

/**
 * VesselMind Chat Component
 * 
 * Main chat interface for the AI assistant.
 * Completely isolated from main app components.
 */
const VesselMindChat = () => {
  const {
    messages,
    isLoading,
    isInitialized,
    hasAskedClarifyingQuestion,
    isAdmin,
    addMessage,
    clearChat,
    setIsLoading,
    initializePdfDocuments,
    setHasAskedClarifyingQuestion
  } = useVesselMind();

  const [inputMessage, setInputMessage] = useState('');
  const [apiKeyReady, setApiKeyReady] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [learningMode, setLearningMode] = useState(false);
  const [showLearningInfo, setShowLearningInfo] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState(''); // For real-time streaming display
  const [isStreaming, setIsStreaming] = useState(false); // Track streaming state
  const [showFAQModal, setShowFAQModal] = useState(false); // FAQ modal visibility
  const [faqModalData, setFaqModalData] = useState(null); // Data for FAQ modal
  const [clarificationContext, setClarificationContext] = useState(null); // Active clarification flow
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isLoadingKnowledgeBase = useRef(false); // Prevent duplicate loads
  const messageSources = useRef({});
  const userQuestions = useRef({});
  const messageMetadata = useRef({});
  
  // Session ID for tracking queries (generated once per session)
  const sessionId = useRef(`session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`).current;

  // Backend knowledge base is the primary system — Anthropic API key is optional
  // (only needed if a query falls through to AI generation fallback)
  useEffect(() => {
    const hasAnthropicKey = isClientInitialized();
    setApiKeyReady(true); // Chat is always usable via backend
    if (!hasAnthropicKey) {
      console.log('[VesselMind] No Anthropic API key — AI generation fallback disabled, backend knowledge base active');
    }
  }, []);

  // Check learning mode status on mount
  useEffect(() => {
    setLearningMode(isLearningModeEnabled());
  }, []);
  
  // Expose diagnostic tool to window for debugging (DEV ONLY)
  useEffect(() => {
    if (knowledgeBase.length > 0) {
      window.vesselMindDiagnostics = (documentTitle, ...searchTerms) => {
        return runDiagnostics(knowledgeBase, documentTitle, searchTerms);
      };
      console.log('🔬 Diagnostics available! Use: window.vesselMindDiagnostics("Code of Practice 22", "stainless steel", "32mm")');
    }
  }, [knowledgeBase]);

  // Mark as initialized immediately — backend knowledge base is always ready.
  // PDF fallback documents load lazily in the background for the rare case
  // where a query falls through to Anthropic API generation.
  useEffect(() => {
    if (!isInitialized && apiKeyReady) {
      initializePdfDocuments([]);
    }
  }, [isInitialized, apiKeyReady, initializePdfDocuments]);

  // Background PDF loading for Anthropic fallback (non-blocking)
  useEffect(() => {
    if (!apiKeyReady || isLoadingKnowledgeBase.current) return;

    const loadFallbackPdfs = async () => {
      isLoadingKnowledgeBase.current = true;
      try {
        const cacheExists = await isCacheValid();
        let kb = cacheExists ? await loadFromCache() : null;

        if (!kb) {
          kb = await loadPdfKnowledgeBase(codesPracticeDocuments);
          await saveToCache(kb);
        }

        setKnowledgeBase(kb);
        console.log(`[VesselMind] Fallback PDF context ready: ${kb.length} documents`);
      } catch (err) {
        console.warn('[VesselMind] PDF fallback load deferred:', err.message);
      }
    };

    loadFallbackPdfs();
  }, [apiKeyReady]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    
    // Rate limiting check (use browser fingerprint as user ID)
    const userId = `browser_${navigator.userAgent.substring(0, 50)}`;
    const rateCheck = checkRateLimit(userId);
    
    if (!rateCheck.allowed) {
      addMessage('assistant', `⚠️ ${rateCheck.message}`);
      console.warn('🚫 Rate limit:', rateCheck.reason);
      return;
    }
    
    // Check concurrent request limit
    if (!startRequest()) {
      addMessage('assistant', '⚠️ Server is busy processing other requests. Please try again in a moment.');
      return;
    }
    
    setInputMessage('');
    
    // IMPORTANT: Get previous questions BEFORE adding current message
    // (because addMessage updates state asynchronously)
    const previousUserMessages = messages.filter(m => m.role === 'user');
    
    // Add user message
    addMessage('user', userMessage);
    setIsLoading(true);
    
    // Record this request
    recordRequest(userId);
    
    try {
      // Step 0: Check FAQ Cache FIRST (instant response if found!)
      console.log('📚 Checking FAQ cache for instant answer...');
      const faqResult = await checkFAQCache(userMessage, sessionId, clarificationContext);
      
      if (faqResult.success && (
        faqResult.responseType === 'knowledge_verified' ||
        faqResult.responseType === 'knowledge_partial' ||
        faqResult.responseType === 'faq_cached'
      )) {
        // Clear any active clarification flow — we got an answer
        if (clarificationContext) setClarificationContext(null);

        const isKnowledge = faqResult.responseType.startsWith('knowledge_');
        const confidence = faqResult.matchScore || 0;
        
        console.log(`✨ ${isKnowledge ? 'Knowledge base' : 'FAQ cache'} HIT! (confidence: ${(confidence * 100).toFixed(1)}%)`);
        console.log(`⚡ Response time: ${faqResult.responseTime}ms`);
        console.log(`💰 API cost: $0.00 (pre-baked)`);
        
        if (isKnowledge && faqResult.source) {
          console.log(`📄 Source: ${faqResult.source.document}, Section ${faqResult.source.section || 'N/A'}`);
        }
        
        let responseMessage = faqResult.answer;
        
        if (isKnowledge && faqResult.source) {
          const sourceRef = `\n\n📄 Source: ${faqResult.source.document}${faqResult.source.section ? `, Section ${faqResult.source.section}` : ''}`;
          responseMessage = faqResult.answer + sourceRef;

          if (faqResult.source.quote?.exactQuote) {
            responseMessage += `\n\n📝 Source text: "${faqResult.source.quote.exactQuote}"`;
          }
        }
        
        const assistantMsg = addMessage('assistant', responseMessage);
        
        messageMetadata.current[assistantMsg.id] = {
          type: faqResult.responseType,
          confidence: confidence,
          confidenceLevel: faqResult.confidenceLevel || (confidence >= 0.85 ? 'high' : confidence >= 0.6 ? 'medium' : 'low'),
          isVerified: faqResult.isVerified || false,
          isPreBaked: faqResult.isPreBaked || false,
          hasConflicts: faqResult.hasConflicts || false,
          conflicts: faqResult.conflicts || [],
          conditions: faqResult.conditions || [],
          matchedQuestion: faqResult.matchedQuestion || null,
          ruleRelations: faqResult.ruleRelations || [],
          audit: faqResult.audit || null
        };
        
        if (faqResult.source) {
          messageSources.current[assistantMsg.id] = [faqResult.source.document];
          userQuestions.current[assistantMsg.id] = userMessage;
        }
        
        setIsLoading(false);
        endRequest();
        return;
      }

      if (faqResult.success && faqResult.responseType === 'clarification_needed') {
        console.log('🔍 VesselMind needs more context — asking clarifying question');

        const assistantMsg = addMessage('assistant', faqResult.answer);

        messageMetadata.current[assistantMsg.id] = {
          type: 'clarification_needed',
          confidence: faqResult.matchScore || 0,
          confidenceLevel: 'pending',
          isVerified: false,
          isPreBaked: false,
          isClarification: true,
          hasConflicts: false,
          conflicts: [],
          candidateCount: faqResult.clarification?.candidateCount || 0
        };

        // Store context so the user's next reply continues the clarification flow
        setClarificationContext({
          originalQuery: userMessage,
          clarifications: {},
          pendingClarification: true
        });

        setIsLoading(false);
        endRequest();
        return;
      }

      if (faqResult.success && faqResult.responseType === 'safety_blocked') {
        console.log('🛡️ Safety-critical query blocked from AI generation');

        const assistantMsg = addMessage('assistant', faqResult.answer);

        messageMetadata.current[assistantMsg.id] = {
          type: 'safety_blocked',
          confidence: 0,
          confidenceLevel: 'none',
          isVerified: false,
          isPreBaked: false,
          isSafetyBlocked: true,
          hasConflicts: false,
          conflicts: []
        };

        setIsLoading(false);
        endRequest();
        return;
      }

      const isExtractiveOnly = faqResult.success && faqResult.extractiveOnly === true;
      if (isExtractiveOnly) {
        console.log('⚠️ Elevated-risk query — AI response restricted to extractive answering only');
      }
      
      console.log('📖 No pre-baked answer found, checking AI generation fallback...');

      // Guard: If no Anthropic API key and no PDF context, inform the user
      if (!isClientInitialized()) {
        addMessage('assistant', 'This question isn\'t covered in the pre-verified knowledge base yet. As the knowledge base grows, more questions will be answered directly.\n\n---\n*Professional consultation with the relevant Code of Practice is recommended.*');
        setIsLoading(false);
        endRequest();
        return;
      }

      // Step 1: Multiple retrieval attempts for redundancy
      console.log('🔍 Starting multi-pass retrieval for:', userMessage);
      console.log(`📊 Rate limit: ${rateCheck.remaining} requests remaining, resets in ${rateCheck.resetIn}s`);
      
      // Step 1.5: Get relevant past Q&A if learning mode is enabled (Step 9)
      const relevantPastQA = getRelevantPastQA(userMessage);
      if (relevantPastQA.length > 0) {
        console.log(`🧠 Learning mode: Found ${relevantPastQA.length} relevant past Q&A pairs`);
      }
      
      // Step 1.6: Build conversational context from previous questions
      // Use previousUserMessages captured BEFORE adding current message
      let enhancedQuery = userMessage;
      
      // Check if there are previous questions
      if (previousUserMessages.length >= 1) {
        // Get last 2 user questions (now previousUserMessages doesn't include current)
        const recentQuestions = previousUserMessages.slice(-2); // Last 2 questions
        
        console.log(`💬 Building context from ${recentQuestions.length} previous question(s)`);
        
        recentQuestions.forEach((msg, idx) => {
          console.log(`   Q${idx + 1}: ${msg.content}`);
        });
        
        // Combine all previous questions for context
        const combinedPreviousQuestions = recentQuestions.map(m => m.content).join(' ');
        enhancedQuery = `${combinedPreviousQuestions} ${userMessage}`;
        
        console.log('🔗 Conversational context enabled');
        console.log('📝 Current query:', userMessage);
        console.log('🔍 Enhanced query with history:', enhancedQuery);
      } else {
        console.log('📝 First question - no previous context available');
      }
      
      // DOCUMENT FILTERING DISABLED - caused accuracy issues by excluding critical documents
      // Always search ALL documents to ensure comprehensive, accurate answers
      console.log(`📚 Searching all ${knowledgeBase.length} documents for maximum accuracy`);
      
      // Attempt 1: Search with conversational context (all documents)
      let searchResults = searchKnowledgeBase(knowledgeBase, enhancedQuery, 5);
      
      console.log(`🔎 Search results: ${searchResults.length} found`);
      if (searchResults.length > 0) {
        console.log('📄 Documents found:', searchResults.map(r => r.documentTitle).join(', '));
      }
      
      // Attempt 2: Semantic search with keywords (use enhanced query for follow-ups, all documents)
      let relevantSections = getRelevantSections(knowledgeBase, enhancedQuery);
      console.log(`📚 Relevant sections: ${relevantSections.length} found`);
      if (relevantSections.length > 0) {
        console.log('📄 Relevant docs:', relevantSections.map(d => `${d.title} (score: ${d.relevanceScore})`).join(', '));
      }
      
      // Attempt 3: Improve ranking using learning data (Step 9)
      if (relevantPastQA.length > 0) {
        relevantSections = improveDocumentRanking(relevantSections, relevantPastQA);
        console.log('📈 Document ranking improved using learning data');
      }
      
      // Combine results and build context
      const contextDocs = new Map();
      
      // Add search results
      searchResults.forEach(result => {
        if (!contextDocs.has(result.documentTitle)) {
          contextDocs.set(result.documentTitle, {
            title: result.documentTitle,
            category: result.category,
            snippets: []
          });
        }
        contextDocs.get(result.documentTitle).snippets.push(result.context);
      });
      
      // Add relevant sections (much larger context for technical documents, especially tables)
      relevantSections.forEach(doc => {
        if (!contextDocs.has(doc.title) && doc.relevanceScore > 0) {
          contextDocs.set(doc.title, {
            title: doc.title,
            category: doc.category,
            // Use the best section (most keyword-dense) or full section (increased from 5000 to 30000 for very large tables)
            snippets: [doc.bestSection || doc.textContent.substring(0, 30000)]
          });
        }
      });
      
      // Step 2: Validate context quality
      const contextArray = Array.from(contextDocs.values());
      
      if (contextArray.length === 0) {
        addMessage('assistant', 'Not covered in the current knowledge base.');
        setIsLoading(false);
        endRequest(); // Release concurrent request slot
        return;
      }
      
      // Build context string
      const contextString = contextArray.map(doc => {
        return `Document: ${doc.title} (${doc.category})\n${doc.snippets.join('\n...\n')}\n`;
      }).join('\n---\n');
      
      // Validate context
      const validation = validateContext(contextString);
      
      if (!validation.valid) {
        console.warn('⚠️ Context validation failed:', validation.reason);
        addMessage('assistant', 'Not covered in the current knowledge base.');
        setIsLoading(false);
        endRequest(); // Release concurrent request slot
        return;
      }
      
      // Step 3: Check confidence threshold
      if (validation.confidence < 0.3) {
        console.warn('⚠️ Low confidence:', validation.confidence);
        addMessage('assistant', 'Not covered in the current knowledge base.');
        setIsLoading(false);
        endRequest(); // Release concurrent request slot
        return;
      }
      
      console.log(`✅ Context validated - Confidence: ${(validation.confidence * 100).toFixed(1)}%, Words: ${validation.wordCount}`);
      
      // Step 3.5: Detect citation requests (Step 7)
      const citationKeywords = ['cite', 'citation', 'source', 'where', 'what document', 'which code', 'reference', 'from which'];
      const userRequestsCitations = citationKeywords.some(keyword => 
        userMessage.toLowerCase().includes(keyword)
      );
      
      // Step 4: Send to Claude with strict controls
      const conversationHistory = messages
        .slice(-6) // Last 3 exchanges
        .map(msg => ({
          role: msg.role,
          content: msg.content
        }));
      
      conversationHistory.push({
        role: 'user',
        content: userMessage
      });
      
      let enhancedContext = contextString;
      if (hasAskedClarifyingQuestion) {
        enhancedContext = `[NOTE: You have already asked a clarifying question in this conversation. Do NOT ask another one.]\n\n${contextString}`;
      }
      if (userRequestsCitations) {
        enhancedContext = `[USER REQUESTING CITATIONS: Include specific document references in your response.]\n\n${enhancedContext}`;
      }
      if (isExtractiveOnly) {
        enhancedContext = `[EXTRACTIVE ONLY: This is a compliance/regulatory query. You MUST only quote or closely paraphrase text that appears in the provided context. Do NOT generate, infer, or synthesise information beyond what is explicitly stated. If the context does not contain a clear answer, say so.]\n\n${enhancedContext}`;
      }
      
      // Step 4.5: Start streaming response (80% perceived speed improvement!)
      let streamedText = '';
      setIsStreaming(true);
      setStreamingMessage('');
      
      // Stream response chunks
      const response = await sendChatCompletionStream(
        conversationHistory,
        enhancedContext,
        (chunk) => {
          // Callback for each text chunk
          streamedText += chunk;
          
          // Update the streaming message in real-time (triggers UI update)
          setStreamingMessage(streamedText);
          setIsLoading(false); // Hide loading spinner once streaming starts
        },
        {
          maxTokens: 2048
          // model defaults to claude-sonnet-4-20250514 in anthropicClient.js
        }
      );
      
      // Streaming complete
      setIsStreaming(false);
      setStreamingMessage('');
      
      if (!response.success) {
        addMessage('assistant', `Error: ${response.error}`);
        setIsLoading(false);
        endRequest(); // Release concurrent request slot
        return;
      }
      
      // Step 5: Validate response doesn't contain hallucination indicators
      const responseText = streamedText || response.content;
      const hallucinationIndicators = [
        'I think',
        'I believe',
        'In my opinion',
        'Generally speaking',
        'It is common',
        'Typically'
      ];
      
      let hasWarning = false;
      hallucinationIndicators.forEach(indicator => {
        if (responseText.toLowerCase().includes(indicator.toLowerCase())) {
          console.warn('⚠️ Potential hallucination detected:', indicator);
          hasWarning = true;
        }
      });
      
      // Step 6: Detect if response is a clarifying question (Step 6)
      const isClarifyingQuestion = responseText.trim().endsWith('?') && responseText.length < 300;
      if (isClarifyingQuestion && !hasAskedClarifyingQuestion) {
        setHasAskedClarifyingQuestion(true);
        console.log('❓ Clarifying question asked - tracking enabled');
      }
      
      // Add response with optional warning
      let finalResponse = responseText;
      if (hasWarning) {
        finalResponse += '\n\n⚠️ Note: This response may contain uncertain information. Please verify against the original documents.';
      }
      
      // Step 7: Only add source citations if user explicitly requested them
      if (userRequestsCitations && contextArray.length > 0) {
        finalResponse += '\n\n📚 Sources: ' + contextArray.map(d => d.title).join(', ');
      }
      
      const assistantMessage = addMessage('assistant', finalResponse);
      
      messageMetadata.current[assistantMessage.id] = {
        type: 'ai_generated',
        confidence: validation?.confidence || 0,
        confidenceLevel: 'none',
        isVerified: false,
        isPreBaked: false,
        hasConflicts: false,
        conflicts: [],
        hasWarning
      };
      
      if (contextArray.length > 0) {
        messageSources.current[assistantMessage.id] = contextArray.map(d => d.title);
        userQuestions.current[assistantMessage.id] = userMessage;
      }
      
      // Log API-generated response to database for admin review
      const responseTime = (Date.now() - Date.parse(new Date())) / 1000;
      logAPIResponse({
        query: userMessage,
        responseText: finalResponse,
        documentsUsed: contextArray.map(d => d.title),
        tokenCount: response.usage?.total_tokens || 0,
        confidenceScore: validation.confidence,
        responseTime,
        sessionId
      }).catch(err => console.warn('Failed to log response:', err));
      
      // Step 9: Store Q&A pair for learning if mode is enabled
      if (learningMode) {
        storeQAPair(
          userMessage,
          responseText, // Store original response, not the one with sources
          contextArray,
          validation.confidence
        );
        console.log('📚 Q&A pair stored for learning');
      }
      
      console.log('✅ Response delivered');
      
      // Log token usage with cache statistics
      const usage = response.usage || {};
      if (usage.cacheReadTokens > 0) {
        console.log('📊 Tokens:', {
          input: usage.inputTokens,
          output: usage.outputTokens,
          cacheRead: usage.cacheReadTokens,
          total: (usage.inputTokens || 0) + (usage.outputTokens || 0)
        });
        console.log(`💰 Cost saved: ~${(usage.cacheReadTokens * 0.9 / 1000000 * 3).toFixed(4)} USD (from cache hit)`);
      } else if (usage.cacheCreationTokens > 0) {
        console.log('📊 Tokens:', {
          input: usage.inputTokens,
          output: usage.outputTokens,
          cached: usage.cacheCreationTokens,
          total: (usage.inputTokens || 0) + (usage.outputTokens || 0)
        });
        console.log('💾 Cache created - next queries will be faster & cheaper!');
      } else {
        console.log('📊 Tokens:', {
          input: usage.inputTokens,
          output: usage.outputTokens,
          total: (usage.inputTokens || 0) + (usage.outputTokens || 0)
        });
      }
      
      console.log('📝 Response logged for admin review');
      if (userRequestsCitations) {
        console.log('📚 Citations included per user request');
      }
      
    } catch (error) {
      console.error('❌ Error processing message:', error);
      addMessage('assistant', 'An error occurred while processing your request. Please try again.');
    } finally {
      setIsLoading(false);
      endRequest(); // Release concurrent request slot
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Toggle learning mode
  const handleToggleLearningMode = () => {
    if (learningMode) {
      disableLearningMode();
      setLearningMode(false);
      console.log('📴 Learning mode disabled');
    } else {
      enableLearningMode();
      setLearningMode(true);
      console.log('📝 Learning mode enabled - storing Q&A pairs');
    }
  };

  // API key setup is no longer a gate — backend handles queries without it

  // Show loading during initialization (should be near-instant now)
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-pvs-accent animate-spin mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Starting VesselMind...
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-pvs-card border border-pvs-accent/30 rounded-xl shadow-pvs-glow flex flex-col h-[calc(100vh-200px)]">
      {/* Chat Header */}
      <div className="border-b border-pvs-border p-4">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-pvs-accent" />
            <span className="font-medium text-white">
              VesselMind — LPG Codes of Practice
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { clearChat(); setClarificationContext(null); }}
              className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors duration-200"
              title="Clear chat"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm">Clear</span>
            </button>
            <button
              onClick={async () => {
                if (!window.confirm('This will permanently delete all your VesselMind query data for this session. Continue?')) return;
                try {
                  const backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
                  const resp = await fetch(`${backendUrl}/api/vesselmind/data/${sessionId}`, { method: 'DELETE' });
                  const result = await resp.json();
                  if (result.success) {
                    clearChat();
                    setClarificationContext(null);
                    alert(`${result.deleted} record(s) deleted.`);
                  }
                } catch (e) { /* silent */ }
              }}
              className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:text-orange-400 hover:bg-orange-900/30 rounded-lg transition-colors duration-200"
              title="Delete all my session data (GDPR)"
            >
              <ShieldX className="w-4 h-4" />
              <span className="text-sm">Delete My Data</span>
            </button>
          </div>
        </div>
        
        {/* Learning Mode Toggle (Step 9) */}
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-pvs-border">
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={learningMode}
                  onChange={handleToggleLearningMode}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pvs-accent/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pvs-accent"></div>
              </div>
              <Brain className={`w-4 h-4 transition-colors ${learningMode ? 'text-pvs-accent' : 'text-pvs-text-dim'}`} />
              <span className={`text-sm font-medium transition-colors ${learningMode ? 'text-pvs-accent' : 'text-pvs-text-muted'}`}>
                Learning Mode {learningMode ? 'ON' : 'OFF'}
              </span>
            </label>
            <button
              onClick={() => setShowLearningInfo(!showLearningInfo)}
              className="text-pvs-text-dim hover:text-pvs-text-muted transition-colors"
              title="About Learning Mode"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
          
          {learningMode && (
            <span className="text-xs text-pvs-text-dim bg-pvs-accent/5 px-2 py-1 rounded">
              {getLearningStats().totalPairs} / {getLearningStats().maxPairs} Q&A pairs stored
            </span>
          )}
        </div>
        
        {/* Learning Mode Info Panel */}
        {showLearningInfo && (
          <div className="mt-3 p-3 bg-pvs-accent/5 rounded-lg border border-pvs-accent/20">
            <h4 className="font-semibold text-sm text-white mb-2">🧠 Learning Mode</h4>
            <ul className="text-xs text-pvs-text-muted space-y-1">
              <li>• When enabled, VesselMind stores anonymized Q&A pairs locally in your browser</li>
              <li>• Uses past conversations to improve document retrieval ranking</li>
              <li>• All data stays in your browser - never sent to servers</li>
              <li>• Automatically removes personal information (names, emails, addresses)</li>
              <li>• When disabled, VesselMind is fully stateless - no history stored</li>
              <li>• Maximum {getLearningStats().maxPairs} pairs stored (oldest removed automatically)</li>
            </ul>
            {learningMode && (
              <button
                onClick={() => {
                  if (window.confirm('Clear all learning data? This cannot be undone.')) {
                    clearLearningData();
                    alert('Learning data cleared!');
                  }
                }}
                className="mt-2 text-xs text-red-400 hover:text-red-300 underline"
              >
                Clear Learning Data
              </button>
            )}
          </div>
        )}
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => {
          const meta = messageMetadata.current[message.id];
          return (
          <div key={message.id}>
            <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-pvs-accent/20 border border-pvs-accent/30 text-white'
                    : 'bg-pvs-card-light border border-pvs-border text-white'
                }`}
              >
                {message.role === 'assistant' && message.id !== 'welcome' && meta && (
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-pvs-border">
                    {meta.isPreBaked && meta.confidenceLevel === 'high' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/30 text-green-400">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                        Consistent with Cited Clauses
                      </span>
                    ) : meta.isPreBaked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-900/20 text-amber-400">
                        Potential Alignment — Verify Applicability
                      </span>
                    ) : meta.isClarification ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-900/30 text-indigo-400">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                        Clarifying Your Question
                      </span>
                    ) : meta.isSafetyBlocked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-900/30 text-red-400">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 1.944A11.954 11.954 0 012.166 5C2.056 5.649 2 6.319 2 7c0 5.225 3.34 9.67 8 11.317C14.66 16.67 18 12.225 18 7c0-.682-.057-1.35-.166-2.001A11.954 11.954 0 0110 1.944zM11 14a1 1 0 11-2 0 1 1 0 012 0zm0-7a1 1 0 10-2 0v3a1 1 0 102 0V7z" clipRule="evenodd"/></svg>
                        Safety Protocol — No Verified Answer Available
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-pvs-accent/10 text-pvs-accent">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/></svg>
                        AI Generated - Verify Against Source
                      </span>
                    )}
                    {meta.isPreBaked && (
                      <span className="text-xs text-pvs-text-dim">
                        {(meta.confidence * 100).toFixed(0)}% match
                      </span>
                    )}
                  </div>
                )}

                <p className="text-sm whitespace-pre-wrap">{message.content}</p>

                {message.role === 'assistant' && meta && meta.hasConflicts && meta.conflicts.length > 0 && (
                  <div className="mt-3 p-2 bg-amber-900/20 border border-amber-500/30 rounded text-xs text-amber-400">
                    <span className="font-medium">Related guidance also found in:</span>
                    <ul className="mt-1 space-y-0.5">
                      {meta.conflicts.map((c, i) => (
                        <li key={i}>
                          {c.document}{c.section ? ` (Section ${c.section})` : ''} — {(c.similarity * 100).toFixed(0)}% match
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1 italic">Requirements may differ by situation. Professional assessment of how these interact is required.</p>
                  </div>
                )}

                {message.role === 'assistant' && meta && meta.ruleRelations && meta.ruleRelations.length > 0 && (
                  <div className="mt-3 p-2 bg-pvs-accent/5 border border-pvs-accent/20 rounded text-xs text-pvs-accent">
                    <span className="font-medium">Related clauses:</span>
                    <ul className="mt-1 space-y-0.5">
                      {meta.ruleRelations.map((r, i) => (
                        <li key={i}>
                          <span className="font-medium capitalize">{r.type.replace(/_/g, ' ')}</span>: {r.relatedDocument}{r.relatedSection ? ` (Section ${r.relatedSection})` : ''}
                          {r.description && <span className="text-pvs-accent"> — {r.description}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {message.role === 'assistant' && meta && meta.audit && (
                  <div className="mt-2 text-[10px] text-pvs-text-dim">
                    VesselMind v{meta.audit.systemVersion} | {meta.audit.searchMethod} | {new Date(meta.audit.timestamp).toLocaleString()}
                  </div>
                )}

                <span className="text-xs opacity-70 mt-1 block">
                  {new Date(message.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>
            
            {/* Add to FAQ button (admin only, assistant messages only) */}
            {isAdmin && message.role === 'assistant' && message.id !== 'welcome' && messageSources.current[message.id] && (
              <div className="flex justify-start mt-1 ml-2">
                <button
                  onClick={() => {
                    setFaqModalData({
                      question: userQuestions.current[message.id] || '',
                      answer: message.content,
                      sources: messageSources.current[message.id] || []
                    });
                    setShowFAQModal(true);
                  }}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-pvs-accent hover:text-pvs-bg hover:bg-pvs-accent rounded transition-colors"
                  title="Add this Q&A to FAQ"
                >
                  <BookOpen className="w-3 h-3" />
                  Add to FAQ
                </button>
              </div>
            )}
          </div>
        );})}
        
        {/* Streaming Message (real-time display) */}
        {isStreaming && streamingMessage && (
          <div className="flex justify-start">
            <div className="max-w-[80%] bg-pvs-card-light border border-pvs-border text-white rounded-lg px-4 py-3">
              <p className="text-sm whitespace-pre-wrap">{streamingMessage}</p>
              <span className="text-xs opacity-70 mt-1 block flex items-center gap-1">
                <span className="inline-block w-2 h-2 bg-pvs-accent rounded-full animate-pulse"></span>
                Streaming...
              </span>
            </div>
          </div>
        )}
        
        {isLoading && !isStreaming && (
          <div className="flex justify-start">
            <div className="bg-pvs-card-light border border-pvs-border rounded-lg px-4 py-3">
              <Loader2 className="w-5 h-5 text-pvs-accent animate-spin" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="border-t border-pvs-border">
        <div className="px-4 py-1.5 bg-amber-900/20 border-b border-amber-500/30">
          <p className="text-xs text-amber-400 text-center">
            Decision support tool — does not constitute a compliance determination. Professional validation required before application.
          </p>
        </div>
        <div className="flex gap-2 p-4">
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything about the Codes of Practice..."
            className="flex-1 px-4 py-3 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim resize-none"
            rows="2"
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send className="w-5 h-5" />
            <span className="font-medium">Send</span>
          </button>
        </div>
        <p className="text-xs text-pvs-text-dim mt-2">
          Press Enter to send, Shift+Enter for new line
        </p>
      </div>
      
      {/* Add to FAQ Modal (admin only) */}
      {showFAQModal && faqModalData && (
        <AddToFAQModal
          question={faqModalData.question}
          answer={faqModalData.answer}
          sources={faqModalData.sources}
          onClose={() => {
            setShowFAQModal(false);
            setFaqModalData(null);
          }}
          onSuccess={(faq) => {
            console.log('✅ FAQ created:', faq);
            // Optional: Show success toast/notification
            alert('FAQ added successfully! Users will now get instant answers for this question.');
          }}
        />
      )}
    </div>
  );
};

export default VesselMindChat;
